/**
 * kontierungApi — WL-K15 client wrappers for the Auto-Kontierung callables.
 *
 * Thin httpsCallable bindings (same pattern as {@link ./clientDatevApi}). ALL hard
 * checks live server-side (`functions/src/moat/kontierungFunctions.ts`):
 * Loop-B-Beta of the caller + an ACTIVE `advisor` membership in the mandant scope,
 * plus the strict field whitelist and the deterministic „memoize last confirmed"
 * engine. These helpers only marshal input/output and normalise errors for the UI.
 * There is NO FE env flag — visibility is gated with `isLoopBBeta(...)` at the
 * component level AND the component is only rendered inside the StB-only
 * AdvisorCockpit (never in the Mandanten-UI).
 *
 * @module api/kontierungApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/** DATEV-Standardkontenrahmen (mirror of the functions-side `KontierungSkr`). */
export type KontierungSkr = 'SKR03' | 'SKR04';

/**
 * The §6e framing (`framing-k15-kontierung`, legal-texts-loop-b.md), embedded 1:1.
 * Kept byte-identical to `KONTIERUNG_FRAMING` in
 * `functions/src/moat/kontierungMemory.ts` — the server also returns it, this const
 * is the client-side fallback/SoT for rendering. DE is maßgeblich.
 */
export const KONTIERUNG_FRAMING =
  'Buchungsvorschlag (automatisch ermittelt). FaeBill schlägt auf Basis der ' +
  'Belegdaten ein Konto und einen Steuerschlüssel vor. Dies ist ein ' +
  'unverbindlicher Vorschlag zur Arbeitserleichterung und keine steuerliche ' +
  'Beratung. Die endgültige Kontierung und steuerliche Würdigung trifft Ihr ' +
  'Steuerberater bzw. die für die Buchhaltung verantwortliche Person.';

/** The StB suggestion read-model (mirror of the functions-side `KontierungVorschlag`). */
export interface KontierungVorschlag {
  hasProposal: boolean;
  skrAccount: string | null;
  buKey: string | null;
  skr: KontierungSkr | null;
  supplierName: string;
  supplierKey: string | null;
  framing: string;
  source: 'memory' | 'none';
}

/** The StB's confirmed booking (all present fields written; buKey optional/nullable). */
export interface KontierungConfigInput {
  skrAccount: string;
  buKey?: string | null;
  skr: KontierungSkr;
}

/** Server result of {@link confirmKontierung}. */
export interface ConfirmKontierungResult {
  status: 'ok';
  supplierKey: string;
  proposal: KontierungVorschlag;
}

/**
 * Loads the last-confirmed booking suggestion for the supplier of one inbound
 * document (read-only). StB-context only — the server returns `permission-denied`
 * for a non-advisor caller.
 *
 * @param mandantUid the mandant (tenant owner) uid.
 * @param docId the inbound document id.
 */
export async function getKontierungVorschlag(
  mandantUid: string,
  docId: string,
): Promise<KontierungVorschlag> {
  const callable = httpsCallable<
    { mandantUid: string; docId: string },
    KontierungVorschlag
  >(functions, 'getKontierungVorschlag');
  try {
    const res = await callable({ mandantUid, docId });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Confirms (or overrides) the booking for the supplier of an inbound document. The
 * choice becomes the new last-confirmed suggestion for that (Kanzlei × Lieferant).
 *
 * @param mandantUid the mandant (tenant owner) uid.
 * @param docId the inbound document id.
 * @param config the confirmed account + Steuerschlüssel + SKR.
 */
export async function confirmKontierung(
  mandantUid: string,
  docId: string,
  config: KontierungConfigInput,
): Promise<ConfirmKontierungResult> {
  const callable = httpsCallable<
    { mandantUid: string; docId: string; config: KontierungConfigInput },
    ConfirmKontierungResult
  >(functions, 'confirmKontierung');
  try {
    const res = await callable({ mandantUid, docId, config });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server message
 * and exposing the coarse function error code (e.g. `permission-denied`).
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
